import React, { useState, useEffect } from "react";
import axios from "axios";
import { Html5QrcodeScanner } from "html5-qrcode";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import useBuildings from "../hooks/useBuildings";
import ImageModal from "../components/ImageModal";
import { API } from "../utils/constants";
import AssetDetailModal from "./assets/AssetDetailModal";
import AssetModal from "./assets/AssetModal";
import AssetEditModal from "./assets/AssetEditModal";
import AssetHistoryPanel from "./assets/AssetHistoryPanel";
import QRScannerModal from "./assets/QRScannerModal";
import AssetsList from "./assets/AssetsList";

const EMPTY_FORM = {
  name: "",
  description: "",
  asset_tag: "",
  location: "",
  category: "",
  assigned_to: "",
  manufacturer: "",
  model: "",
  serial_number: "",
  building_id: null,
  images: [],
};

const AssetsPage = () => {
  const { hasPermission } = useAuth();
  const { buildings, buildingsLoading, buildingsError, fetchBuildings } = useBuildings();

  // Asset list state
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Selected / detail modal
  const [selectedAsset, setSelectedAsset] = useState(null);

  // Add modal
  const [showAddAsset, setShowAddAsset] = useState(false);

  // Edit modal
  const [showEditAsset, setShowEditAsset] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);

  // Shared form
  const [assetForm, setAssetForm] = useState(EMPTY_FORM);

  // History modal
  const [showAssetHistory, setShowAssetHistory] = useState(false);
  const [assetHistory, setAssetHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // QR scanner
  const [showScanner, setShowScanner] = useState(false);

  // Image lightbox
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalImages, setModalImages] = useState([]);

  // ── Initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    fetchAssets();
    if (buildings.length === 0) fetchBuildings();
  }, []);

  // ── QR Scanner ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!showScanner) return;

    const isMobileView = window.innerWidth < 768;
    const scannerWidth = isMobileView ? Math.min(280, window.innerWidth - 80) : 320;
    const scannerHeight = isMobileView ? Math.min(280, window.innerWidth - 80) : 320;

    const scanner = new Html5QrcodeScanner(
      "qr-scanner",
      {
        fps: 10,
        qrbox: { width: scannerWidth * 0.8, height: scannerHeight * 0.8 },
        aspectRatio: 1.0,
        disableFlip: false,
        rememberLastUsedCamera: true,
        supportedScanTypes: [0, 1],
      },
      false
    );

    scanner.render(
      (decodedText) => {
        handleQRScan(decodedText);
        scanner.clear();
        setShowScanner(false);
      },
      () => {}
    );

    return () => { scanner.clear(); };
  }, [showScanner]);

  // ── Cleanup blob URLs on unmount ─────────────────────────────────────────
  useEffect(() => {
    return () => {
      assetForm.images?.forEach(img => {
        if (img.temporary && img.preview?.startsWith("blob:")) {
          URL.revokeObjectURL(img.preview);
        }
      });
      modalImages?.forEach(img => {
        if (img.temporary && img.preview?.startsWith("blob:")) {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, []);

  // Load buildings when add modal opens
  useEffect(() => {
    if (showAddAsset && buildings.length === 0) fetchBuildings();
  }, [showAddAsset]);

  // ── Data functions ────────────────────────────────────────────────────────
  const fetchAssets = async () => {
    try {
      setLoading(true);
      const { retryOperation } = await import("../utils/networkResilience");
      const response = await retryOperation(
        () => axios.get(`${API}/assets`),
        { operationName: "fetch_assets", maxRetries: 3 }
      );
      setAssets(response.data);
    } catch (error) {
      console.error("Error fetching assets:", error);
      const { parseBackendError, getErrorMessage } = await import("../utils/errorHandling");
      const parsed = parseBackendError(error, { operation: "fetch_assets" });
      toast.error(`Failed to load assets: ${getErrorMessage(parsed.type, parsed.context).message}`);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const createAsset = async () => {
    if (!assetForm.name.trim()) { toast.error("Asset name is required"); return; }
    if (!assetForm.building_id) { toast.error("Property assignment is required"); return; }

    try {
      setLoading(true);
      const formData = { ...assetForm };
      if (!formData.asset_tag.trim()) formData.asset_tag = `ASSET-${Date.now()}`;
      formData.building_id = String(formData.building_id);

      const apiFormData = new FormData();
      apiFormData.append("name", formData.name);
      apiFormData.append("asset_tag", formData.asset_tag);
      apiFormData.append("building_id", formData.building_id);
      if (formData.description) apiFormData.append("description", formData.description);
      if (formData.location) apiFormData.append("location", formData.location);
      if (formData.category) apiFormData.append("category", formData.category);
      if (formData.assigned_to) apiFormData.append("assigned_to", formData.assigned_to);
      if (formData.manufacturer) apiFormData.append("manufacturer", formData.manufacturer);
      if (formData.model) apiFormData.append("model", formData.model);
      if (formData.serial_number) apiFormData.append("serial_number", formData.serial_number);

      const temporaryImages = assetForm.images?.filter(img => img.temporary && img.file) || [];
      temporaryImages.forEach(img => apiFormData.append("images", img.file));

      const { retryUploadOperation } = await import("../utils/networkResilience");
      const response = await retryUploadOperation(
        () => axios.post(`${API}/assets`, apiFormData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        }),
        { operationName: "create_asset_with_images", maxRetries: temporaryImages.length > 0 ? 2 : 3 }
      );

      toast.success(
        temporaryImages.length > 0
          ? `Asset created with ${temporaryImages.length} image${temporaryImages.length > 1 ? "s" : ""}!`
          : "Asset created successfully!"
      );
      setAssets(prev => [response.data, ...prev]);
      resetAssetForm();
      setShowAddAsset(false);
    } catch (error) {
      console.error("Error creating asset:", error);
      const { parseBackendError, getErrorMessage } = await import("../utils/errorHandling");
      const parsed = parseBackendError(error, { operation: "create_asset", assetName: assetForm.name });
      toast.error(getErrorMessage(parsed.type, parsed.context).message);
      assetForm.images?.forEach(img => {
        if (img.temporary && img.preview?.startsWith("blob:")) URL.revokeObjectURL(img.preview);
      });
    } finally {
      setLoading(false);
    }
  };

  const updateAsset = async () => {
    if (!assetForm.name.trim()) { toast.error("Asset name is required"); return; }
    if (!assetForm.building_id) { toast.error("Property assignment is required"); return; }

    try {
      setLoading(true);
      const formData = { ...assetForm, building_id: String(assetForm.building_id) };

      const apiFormData = new FormData();
      apiFormData.append("name", formData.name);
      apiFormData.append("asset_tag", formData.asset_tag);
      apiFormData.append("building_id", formData.building_id);
      if (formData.description) apiFormData.append("description", formData.description);
      if (formData.location) apiFormData.append("location", formData.location);
      if (formData.category) apiFormData.append("category", formData.category);
      if (formData.assigned_to) apiFormData.append("assigned_to", formData.assigned_to);
      if (formData.manufacturer) apiFormData.append("manufacturer", formData.manufacturer);
      if (formData.model) apiFormData.append("model", formData.model);
      if (formData.serial_number) apiFormData.append("serial_number", formData.serial_number);

      const temporaryImages = assetForm.images?.filter(img => img.temporary && img.file) || [];
      temporaryImages.forEach(img => apiFormData.append("new_images", img.file));

      const { retryUploadOperation } = await import("../utils/networkResilience");
      const response = await retryUploadOperation(
        () => axios.put(`${API}/assets/${editingAsset.id}`, apiFormData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        }),
        { operationName: "update_asset_with_images", maxRetries: temporaryImages.length > 0 ? 2 : 3 }
      );

      toast.success(
        temporaryImages.length > 0
          ? `Asset updated with ${temporaryImages.length} new image${temporaryImages.length > 1 ? "s" : ""}!`
          : "Asset updated successfully!"
      );
      setAssets(prev => prev.map(a => a.id === editingAsset.id ? response.data : a));
      resetAssetForm();
      setShowEditAsset(false);
      setEditingAsset(null);
    } catch (error) {
      console.error("Error updating asset:", error);
      const { parseBackendError, getErrorMessage } = await import("../utils/errorHandling");
      const parsed = parseBackendError(error, { operation: "update_asset", assetId: editingAsset?.id });
      toast.error(getErrorMessage(parsed.type, parsed.context).message);
    } finally {
      setLoading(false);
    }
  };

  const deleteAsset = async (assetId) => {
    try {
      setLoading(true);
      const { retryOperation } = await import("../utils/networkResilience");
      await retryOperation(
        () => axios.delete(`${API}/assets/${assetId}`),
        { operationName: "delete_asset", maxRetries: 2 }
      );
      setAssets(prev => prev.filter(a => a.id !== assetId));
      setSelectedAsset(null);
      toast.success("Asset deleted successfully!");
    } catch (error) {
      console.error("Error deleting asset:", error);
      const { parseBackendError, getErrorMessage } = await import("../utils/errorHandling");
      const parsed = parseBackendError(error, { operation: "delete_asset", assetId });
      toast.error(`Failed to delete asset: ${getErrorMessage(parsed.type, parsed.context).message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetHistory = async (assetId) => {
    try {
      setHistoryLoading(true);
      const { retryOperation } = await import("../utils/networkResilience");
      const response = await retryOperation(
        () => axios.get(`${API}/assets/${assetId}/history`),
        { operationName: "fetch_asset_history", maxRetries: 2 }
      );
      setAssetHistory(response.data);
    } catch (error) {
      console.error("Error fetching asset history:", error);
      const { parseBackendError, getErrorMessage } = await import("../utils/errorHandling");
      const parsed = parseBackendError(error, { operation: "fetch_asset_history", assetId });
      toast.error(`Failed to load asset history: ${getErrorMessage(parsed.type, parsed.context).message}`);
      setAssetHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleQRScan = async (scanResult) => {
    try {
      if (!scanResult.startsWith("ASSET:")) {
        toast.error("Invalid QR code format. Please scan a valid asset QR code.");
        return;
      }
      const assetId = scanResult.replace("ASSET:", "");
      const { retryOperation } = await import("../utils/networkResilience");
      const response = await retryOperation(
        () => axios.get(`${API}/assets/${assetId}`),
        { operationName: "fetch_scanned_asset", maxRetries: 2 }
      );
      setSelectedAsset(response.data);
      toast.success("Asset found!");
    } catch (error) {
      console.error("Error fetching scanned asset:", error);
      const { parseBackendError, getErrorMessage } = await import("../utils/errorHandling");
      const parsed = parseBackendError(error, { operation: "fetch_scanned_asset", scanResult });
      if (parsed.type === "ASSET_NOT_FOUND") {
        toast.error("Asset not found. The QR code may be invalid or the asset may have been deleted.");
      } else {
        toast.error(`Failed to load asset: ${getErrorMessage(parsed.type, parsed.context).message}`);
      }
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const resetAssetForm = () => {
    assetForm.images?.forEach(img => {
      if (img.temporary && img.preview?.startsWith("blob:")) URL.revokeObjectURL(img.preview);
    });
    setAssetForm(EMPTY_FORM);
  };

  const openEditModal = (asset) => {
    setEditingAsset(asset);
    setAssetForm({
      name: asset.name || "",
      description: asset.description || "",
      asset_tag: asset.asset_tag || "",
      location: asset.location || "",
      category: asset.category || "",
      assigned_to: asset.assigned_to || "",
      manufacturer: asset.manufacturer || "",
      model: asset.model || "",
      serial_number: asset.serial_number || "",
      building_id: asset.building_id || null,
      images: asset.image_metadata || [],
    });
    setShowEditAsset(true);
    if (buildings.length === 0) fetchBuildings();
  };

  const openHistoryModal = (asset) => {
    setEditingAsset(asset);
    setShowAssetHistory(true);
    fetchAssetHistory(asset.id);
  };

  const handleImageView = (imageIndex) => {
    try {
      const images = assetForm.images || [];
      if (imageIndex >= 0 && imageIndex < images.length) {
        setCurrentImageIndex(imageIndex);
        setModalImages(images);
        setShowImageModal(true);
      } else {
        toast.error("Unable to display image");
      }
    } catch (error) {
      toast.error("Failed to open image viewer");
    }
  };

  const handleImageRemove = async (imageId) => {
    try {
      const imageToRemove = assetForm.images.find(img => img.id === imageId);
      if (!imageToRemove) { toast.error("Image not found"); return; }

      if (!imageToRemove.temporary && editingAsset) {
        try {
          const { retryOperation } = await import("../utils/networkResilience");
          await retryOperation(
            () => axios.delete(`${API}/assets/${editingAsset.id}/images/${imageId}`),
            { operationName: "remove_image", maxRetries: 2 }
          );
          toast.success("Image removed successfully");
        } catch (deleteError) {
          const { parseBackendError, getErrorMessage } = await import("../utils/errorHandling");
          const parsed = parseBackendError(deleteError, { operation: "remove_image", imageId, assetId: editingAsset.id });
          toast.error(getErrorMessage(parsed.type, parsed.context).message);
          return;
        }
      }

      if (imageToRemove.temporary && imageToRemove.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(imageToRemove.preview);
      }

      setAssetForm(prev => ({ ...prev, images: prev.images.filter(img => img.id !== imageId) }));
      if (imageToRemove.temporary) toast.success("Image removed");
    } catch (error) {
      toast.error("Failed to remove image");
    }
  };

  const handleImagesChange = (newImages) => {
    if (!Array.isArray(newImages)) return;
    const removed = (assetForm.images || []).filter(cur => !newImages.some(n => n.id === cur.id));
    removed.forEach(img => {
      if (img.temporary && img.preview?.startsWith("blob:")) URL.revokeObjectURL(img.preview);
    });
    setAssetForm(prev => ({ ...prev, images: newImages }));
  };

  const formatHistoryDate = (dateString) => new Date(dateString).toLocaleString();
  const formatFieldName = (fieldName) =>
    fieldName.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());

  const filteredAssets = assets.filter(asset =>
    asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (asset.location && asset.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (asset.category && asset.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (asset.asset_tag && asset.asset_tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (asset.manufacturer && asset.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (asset.model && asset.model.toLowerCase().includes(searchTerm.toLowerCase()))
  );


  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Assets</h1>
      </div>

      <AssetsList
        assets={assets}
        filteredAssets={filteredAssets}
        loading={loading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showScanner={showScanner}
        setShowScanner={setShowScanner}
        setShowAddAsset={setShowAddAsset}
        hasPermission={hasPermission}
        buildings={buildings}
        setSelectedAsset={setSelectedAsset}
        openEditModal={openEditModal}
        openHistoryModal={openHistoryModal}
      />

      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScannerModal setShowScanner={setShowScanner} />
      )}

      {/* Add Asset Modal */}
      {showAddAsset && (
        <AssetModal
          assetForm={assetForm}
          setAssetForm={setAssetForm}
          createAsset={createAsset}
          loading={loading}
          setShowAddAsset={setShowAddAsset}
          buildings={buildings}
          buildingsLoading={buildingsLoading}
          buildingsError={buildingsError}
        />
      )}

      {/* Asset Detail Modal */}
      {selectedAsset && (
        <AssetDetailModal
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          buildings={buildings}
          setCurrentImageIndex={setCurrentImageIndex}
          setModalImages={setModalImages}
          setShowImageModal={setShowImageModal}
          deleteAsset={deleteAsset}
          loading={loading}
          hasPermission={hasPermission}
        />
      )}

      {/* Edit Asset Modal */}
      {showEditAsset && editingAsset && (
        <AssetEditModal
          assetForm={assetForm}
          setAssetForm={setAssetForm}
          editingAsset={editingAsset}
          setEditingAsset={setEditingAsset}
          updateAsset={updateAsset}
          loading={loading}
          setShowEditAsset={setShowEditAsset}
          resetAssetForm={resetAssetForm}
          buildings={buildings}
          buildingsLoading={buildingsLoading}
          buildingsError={buildingsError}
          handleImageRemove={handleImageRemove}
          handleImagesChange={handleImagesChange}
        />
      )}

      {/* Asset History Modal */}
      {showAssetHistory && editingAsset && (
        <AssetHistoryPanel
          editingAsset={editingAsset}
          setEditingAsset={setEditingAsset}
          assetHistory={assetHistory}
          setAssetHistory={setAssetHistory}
          historyLoading={historyLoading}
          setShowAssetHistory={setShowAssetHistory}
          formatHistoryDate={formatHistoryDate}
          formatFieldName={formatFieldName}
        />
      )}

      {/* Image Lightbox */}
      <ImageModal
        images={modalImages}
        currentIndex={currentImageIndex}
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onNavigate={setCurrentImageIndex}
      />
    </div>
  );
};

export default AssetsPage;
